import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, useCartRefresh } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const { user, token, logout } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const { cartRefreshKey } = useCartRefresh();
  const navigate = useNavigate();

  // Handle scroll to hide/show navbar
  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        // Scrolling up or at top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsVisible(false);
        setActiveMenu(null); // Close any open menus
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlNavbar);
    return () => window.removeEventListener('scroll', controlNavbar);
  }, [lastScrollY]);

  const fetchCart = async () => {
    if (user) {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${API}/cart`, config);
        setCartItems(response.data.items || []);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }
  };

  useEffect(() => {
    fetchCart();
  }, [user, cartRefreshKey]);

  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  return (
    <>
      {/* Top Bar - Help, Orders, Login */}
      <div className="bg-gray-100 border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-16">
          <div className="flex justify-end items-center h-9 text-xs space-x-4">
            <Link to="#" className="hover:text-gray-600 transition-colors">
              Help
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/orders" className="hover:text-gray-600 transition-colors">
              Orders and returns
            </Link>
            <span className="text-gray-300">|</span>
            {user ? (
              <>
                <div className="relative group">
                  <button className="hover:text-gray-600 transition-colors flex items-center space-x-1">
                    <span>Hi, {user.full_name?.split(' ')[0] || user.full_name}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </button>
                  
                  {/* Account Dropdown */}
                  <div className="hidden group-hover:block absolute right-0 top-full pt-2 z-50">
                    <div className="w-48 bg-white border border-gray-200 shadow-lg rounded-lg py-2">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="font-semibold text-sm">Account</p>
                      </div>
                      <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Profile
                      </Link>
                      <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Orders
                      </Link>
                      <Link to="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Favourites
                      </Link>
                      <Link to="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Experiences
                      </Link>
                      <Link to="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Account Settings
                      </Link>
                      <button 
                        onClick={logout} 
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link to="/register" className="hover:text-gray-600 transition-colors">
                  Sign up
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/login" className="hover:text-gray-600 transition-colors">
                  Log in
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <header className={`bg-white sticky top-0 z-40 shadow-sm transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-16">
          <div className="flex items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3 cursor-pointer flex-shrink-0" onClick={() => navigate('/')}>
              <img src="/nasiX/fulllogo_transparent_nobuffer.png" alt="nasiX" className="h-12 object-contain" />
            </div>
            
            {/* Main Navigation - Centered */}
            <nav className="hidden lg:flex items-center justify-center space-x-8 absolute left-1/2 -translate-x-1/2">
              <div 
                className="relative group"
                onMouseEnter={() => setActiveMenu('featured')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <div className="text-sm font-semibold hover:text-gray-600 transition-colors uppercase tracking-wide py-6 inline-block border-b-2 border-transparent group-hover:border-black cursor-pointer">
                  New & Featured
                </div>
              </div>
              
              <div 
                className="relative group"
                onMouseEnter={() => setActiveMenu('men')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <div className="text-sm font-semibold hover:text-gray-600 transition-colors uppercase tracking-wide py-6 inline-block border-b-2 border-transparent group-hover:border-black cursor-pointer">
                  Men
                </div>
              </div>
              
              <div 
                className="relative group"
                onMouseEnter={() => setActiveMenu('women')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <div className="text-sm font-semibold hover:text-gray-600 transition-colors uppercase tracking-wide py-6 inline-block border-b-2 border-transparent group-hover:border-black cursor-pointer">
                  Women
                </div>
              </div>
              
              <div 
                className="relative group"
                onMouseEnter={() => setActiveMenu('kids')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <div className="text-sm font-semibold hover:text-gray-600 transition-colors uppercase tracking-wide py-6 inline-block border-b-2 border-transparent group-hover:border-black cursor-pointer">
                  Kids
                </div>
              </div>
              
              <div 
                className="relative group"
                onMouseEnter={() => setActiveMenu('sale')}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <div className="text-sm font-semibold hover:text-gray-600 transition-colors uppercase tracking-wide py-6 inline-block border-b-2 border-transparent group-hover:border-black cursor-pointer">
                  Sale
                </div>
              </div>
              
              {user?.role === 'admin' && (
                <Link to="/admin" className="text-sm font-semibold text-red-600 hover:text-red-700 transition-colors uppercase tracking-wide">
                  Admin
                </Link>
              )}
            </nav>
            
            {/* Right Section - Search, Wishlist, Cart */}
            <div className="flex items-center space-x-4 ml-auto">
              {/* Search - Desktop */}
              <div className="hidden md:block relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                  placeholder="Search"
                  className="bg-gray-100 rounded-full px-4 py-2 pl-10 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Search Icon - Mobile */}
              <button 
                onClick={() => setMobileSearchOpen(true)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              
              {/* Wishlist */}
              <button className="p-2 hover:bg-gray-100 rounded-full transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              
              {/* Cart */}
              <Link to="/cart" className="relative p-2 hover:bg-gray-100 rounded-full transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {cartItemCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mega Menu Dropdowns */}
        {activeMenu && (
          <div 
            className="absolute left-0 right-0 bg-white border-t border-gray-200 shadow-lg"
            onMouseEnter={() => setActiveMenu(activeMenu)}
            onMouseLeave={() => setActiveMenu(null)}
          >
            <div className="max-w-screen-2xl mx-auto px-16 py-8">
              {/* New & Featured Menu */}
              {activeMenu === 'featured' && (
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <h3 className="font-bold text-sm mb-3">Featured</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">New & Upcoming Drops</Link></li>
                      <li><Link to="/products" className="hover:text-black">New Arrivals</Link></li>
                      <li><Link to="/products" className="hover:text-black">Bestsellers</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Trending</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Ready for the Cold</Link></li>
                      <li><Link to="/products" className="hover:text-black">More Colours, More Running</Link></li>
                      <li><Link to="/products" className="hover:text-black">What's Trending</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Shop Icons</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Lifestyle</Link></li>
                      <li><Link to="/products" className="hover:text-black">Air Force 1</Link></li>
                      <li><Link to="/products" className="hover:text-black">Air Max</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Shop By Sport</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products?category=Sports" className="hover:text-black">Running</Link></li>
                      <li><Link to="/products?category=Sports" className="hover:text-black">Basketball</Link></li>
                      <li><Link to="/products?category=Sports" className="hover:text-black">Football</Link></li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Men Menu */}
              {activeMenu === 'men' && (
                <div className="grid grid-cols-4 gap-6">
                  <div>
                    <Link to="/topwear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block">Topwear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/topwear/tshirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>T-Shirts</Link></li>
                      <li><Link to="/mens/topwear/casual-shirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Casual Shirts</Link></li>
                      <li><Link to="/mens/topwear/formal-shirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Formal Shirts</Link></li>
                      <li><Link to="/mens/topwear/sweatshirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Sweatshirts</Link></li>
                      <li><Link to="/mens/topwear/sweaters" className="hover:text-black" onClick={() => setActiveMenu(null)}>Sweaters</Link></li>
                      <li><Link to="/mens/topwear/jackets" className="hover:text-black" onClick={() => setActiveMenu(null)}>Jackets</Link></li>
                      <li><Link to="/mens/topwear/blazers-coats" className="hover:text-black" onClick={() => setActiveMenu(null)}>Blazers & Coats</Link></li>
                      <li><Link to="/mens/topwear/suits" className="hover:text-black" onClick={() => setActiveMenu(null)}>Suits</Link></li>
                      <li><Link to="/mens/topwear/rain-jackets" className="hover:text-black" onClick={() => setActiveMenu(null)}>Rain Jackets</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/bottomwear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block">Bottomwear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/bottomwear/jeans" className="hover:text-black" onClick={() => setActiveMenu(null)}>Jeans</Link></li>
                      <li><Link to="/mens/bottomwear/casual-trousers" className="hover:text-black" onClick={() => setActiveMenu(null)}>Casual Trousers</Link></li>
                      <li><Link to="/mens/bottomwear/formal-trousers" className="hover:text-black" onClick={() => setActiveMenu(null)}>Formal Trousers</Link></li>
                      <li><Link to="/mens/bottomwear/shorts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Shorts</Link></li>
                      <li><Link to="/mens/bottomwear/track-pants-joggers" className="hover:text-black" onClick={() => setActiveMenu(null)}>Track Pants & Joggers</Link></li>
                    </ul>
                    <Link to="/topwear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 mt-6 text-red-500 hover:text-red-700 block">Plus Size</Link>
                  </div>
                  <div>
                    <Link to="/indian-wear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block">Indian & Festive Wear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/indian-wear/kurtas-kurta-sets" className="hover:text-black" onClick={() => setActiveMenu(null)}>Kurtas & Kurta Sets</Link></li>
                      <li><Link to="/mens/indian-wear/sherwanis" className="hover:text-black" onClick={() => setActiveMenu(null)}>Sherwanis</Link></li>
                      <li><Link to="/mens/indian-wear/nehru-jackets" className="hover:text-black" onClick={() => setActiveMenu(null)}>Nehru Jackets</Link></li>
                      <li><Link to="/mens/indian-wear/dhotis" className="hover:text-black" onClick={() => setActiveMenu(null)}>Dhotis</Link></li>
                    </ul>
                    <Link to="/sports-wear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 mt-6 text-red-500 hover:text-red-700 block">Sports & Active Wear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/sports-active-wear/active-tshirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Active T-Shirts</Link></li>
                      <li><Link to="/mens/sports-active-wear/track-pants-shorts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Track Pants & Shorts</Link></li>
                      <li><Link to="/mens/sports-active-wear/tracksuits" className="hover:text-black" onClick={() => setActiveMenu(null)}>Tracksuits</Link></li>
                      <li><Link to="/mens/sports-active-wear/jackets-sweatshirts" className="hover:text-black" onClick={() => setActiveMenu(null)}>Jackets & Sweatshirts</Link></li>
                      <li><Link to="/mens/sports-active-wear/swimwear" className="hover:text-black" onClick={() => setActiveMenu(null)}>Swimwear</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/innerwear-sleepwear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block">Innerwear & Sleepwear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/innerwear-sleepwear/briefs-trunks" className="hover:text-black" onClick={() => setActiveMenu(null)}>Briefs & Trunks</Link></li>
                      <li><Link to="/mens/innerwear-sleepwear/boxers" className="hover:text-black" onClick={() => setActiveMenu(null)}>Boxers</Link></li>
                      <li><Link to="/mens/innerwear-sleepwear/vests" className="hover:text-black" onClick={() => setActiveMenu(null)}>Vests</Link></li>
                      <li><Link to="/mens/innerwear-sleepwear/sleepwear-loungewear" className="hover:text-black" onClick={() => setActiveMenu(null)}>Sleepwear & Loungewear</Link></li>
                      <li><Link to="/mens/innerwear-sleepwear/thermals" className="hover:text-black" onClick={() => setActiveMenu(null)}>Thermals</Link></li>
                    </ul>
                    <Link to="/fashion-accessories" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 mt-6 text-red-500 hover:text-red-700 block">Fashion Accessories</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/mens/fashion-accessories/ties-cufflinks-pocket-squares" className="hover:text-black" onClick={() => setActiveMenu(null)}>Ties, Cufflinks & Pocket Squares</Link></li>
                      <li><Link to="/mens/fashion-accessories/caps-hats" className="hover:text-black" onClick={() => setActiveMenu(null)}>Caps & Hats</Link></li>
                      <li><Link to="/mens/fashion-accessories/mufflers-scarves-gloves" className="hover:text-black" onClick={() => setActiveMenu(null)}>Mufflers, Scarves & Gloves</Link></li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Women Menu */}
              {activeMenu === 'women' && (
                <div className="grid grid-cols-4 gap-6">
                  <div>
                    <Link to="/indian-fusion-wear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block">Indian & Fusion Wear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Kurtas & Suits</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Kurtis, Tunics & Tops</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Sarees</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Ethnic Wear</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Leggings, Salwars & Churidars</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Skirts & Palazzos</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Dress Materials</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Lehenga Cholis</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Dupattas & Shawls</Link></li>
                      <li><Link to="/indian-fusion-wear" className="hover:text-black">Jackets</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/western-wear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block">Western Wear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/western-wear" className="hover:text-black">Dresses</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Tops</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Tshirts</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Jeans</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Trousers & Capris</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Shorts & Skirts</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Co-ords</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Playsuits</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Jumpsuits</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Shrugs</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Sweaters & Sweatshirts</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Jackets & Coats</Link></li>
                      <li><Link to="/western-wear" className="hover:text-black">Blazers & Waistcoats</Link></li>
                    </ul>
                    <h3 className="font-bold text-sm mb-3 mt-6 text-pink-600">Plus Size</h3>
                    <h3 className="font-bold text-sm mb-3 mt-4 text-pink-600">Maternity</h3>
                  </div>
                  <div>
                    <Link to="/sports-active-wear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block">Sports & Active Wear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/sports-active-wear" className="hover:text-black">Sports T-Shirts & Tops</Link></li>
                      <li><Link to="/sports-active-wear" className="hover:text-black">Track Pants & Shorts</Link></li>
                      <li><Link to="/sports-active-wear" className="hover:text-black">Tracksuits</Link></li>
                      <li><Link to="/sports-active-wear" className="hover:text-black">Sports Jackets</Link></li>
                      <li><Link to="/sports-active-wear" className="hover:text-black">Swimwear</Link></li>
                    </ul>
                    <Link to="/lingerie-sleepwear" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 mt-6 text-pink-600 hover:text-pink-800 block">Lingerie & Sleepwear</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Bra</Link></li>
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Briefs</Link></li>
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Shapewear</Link></li>
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Sleepwear & Loungewear</Link></li>
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Swimwear</Link></li>
                      <li><Link to="/lingerie-sleepwear" className="hover:text-black">Camisoles & Thermals</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/women-fashion-accessories" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block">Fashion Accessories</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/women-fashion-accessories" className="hover:text-black">Scarves & Stoles</Link></li>
                      <li><Link to="/women-fashion-accessories" className="hover:text-black">Caps & Hats</Link></li>
                      <li><Link to="/women-fashion-accessories" className="hover:text-black">Gloves</Link></li>
                    </ul>
                  </div>
                </div>
              )}


              {/* Kids Menu */}
              {activeMenu === 'kids' && (
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <Link to="/boys-clothing" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block">Boys Clothing</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/boys-clothing" className="hover:text-black">T-Shirts</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Shirts</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Shorts</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Jeans</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Trousers</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Clothing Sets</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Ethnic Wear</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Track Pants & Pyjamas</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Jacket, Sweater & Sweatshirts</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Party Wear</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Innerwear & Thermals</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Nightwear & Loungewear</Link></li>
                      <li><Link to="/boys-clothing" className="hover:text-black">Value Packs</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/girls-clothing" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block">Girls Clothing</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Dresses</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Tops</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Tshirts</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Clothing Sets</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Lehenga choli</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Kurta Sets</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Party wear</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Dungarees & Jumpsuits</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Skirts & shorts</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Tights & Leggings</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Jeans, Trousers & Capris</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Jacket, Sweater & Sweatshirts</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Innerwear & Thermals</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Nightwear & Loungewear</Link></li>
                      <li><Link to="/girls-clothing" className="hover:text-black">Value Packs</Link></li>
                    </ul>
                  </div>
                  <div>
                    <Link to="/infants" onClick={() => setActiveMenu(null)} className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block">Infants</Link>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/infants" className="hover:text-black">Bodysuits</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Rompers & Sleepsuits</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Clothing Sets</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Tshirts & Tops</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Dresses</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Bottom wear</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Winter Wear</Link></li>
                      <li><Link to="/infants" className="hover:text-black">Innerwear & Sleepwear</Link></li>
                    </ul>
                    <h3 className="font-bold text-sm mb-3 mt-6 text-orange-600">Brands</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">H&M</Link></li>
                      <li><Link to="/products" className="hover:text-black">Max Kids</Link></li>
                      <li><Link to="/products" className="hover:text-black">Pantaloons</Link></li>
                      <li><Link to="/products" className="hover:text-black">United Colors Of Benetton Kids</Link></li>
                      <li><Link to="/products" className="hover:text-black">YK</Link></li>
                      <li><Link to="/products" className="hover:text-black">U.S. Polo Assn. Kids</Link></li>
                      <li><Link to="/products" className="hover:text-black">Mothercare</Link></li>
                      <li><Link to="/products" className="hover:text-black">HRX</Link></li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Sale Menu */}
              {activeMenu === 'sale' && (
                <div className="grid grid-cols-5 gap-4">
                  <div>
                    <h3 className="font-bold text-sm mb-3">Sale & Offers</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Shop All Sale</Link></li>
                      <li><Link to="/products" className="hover:text-black">Bestsellers</Link></li>
                      <li><Link to="/products" className="hover:text-black">Last Chance</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Men's Sale</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Shoes</Link></li>
                      <li><Link to="/products" className="hover:text-black">Clothing</Link></li>
                      <li><Link to="/products?category=Accessories" className="hover:text-black">Accessories & Equipment</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Women's Sale</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Shoes</Link></li>
                      <li><Link to="/products" className="hover:text-black">Clothing</Link></li>
                      <li><Link to="/products?category=Accessories" className="hover:text-black">Accessories & Equipment</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Kids' Sale</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Shoes</Link></li>
                      <li><Link to="/products" className="hover:text-black">Clothing</Link></li>
                      <li><Link to="/products?category=Accessories" className="hover:text-black">Accessories & Equipment</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-3">Shop By Sports</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li><Link to="/products" className="hover:text-black">Football</Link></li>
                      <li><Link to="/products" className="hover:text-black">Running</Link></li>
                      <li><Link to="/products" className="hover:text-black">Basketball</Link></li>
                      <li><Link to="/products" className="hover:text-black">Gym & Training</Link></li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 bg-white z-50 md:hidden">
          <div className="flex items-center p-4 border-b">
            <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              placeholder="Search"
              className="flex-1 text-base focus:outline-none"
              autoFocus
            />
            <button 
              onClick={() => {
                setMobileSearchOpen(false);
                setSearchQuery('');
              }}
              className="text-sm font-medium ml-4"
            >
              Cancel
            </button>
          </div>

          {/* Popular Search Terms */}
          <div className="p-6">
            <h3 className="text-sm font-medium mb-4 text-gray-600">Popular Search Terms</h3>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => {
                  setSearchQuery('shoes');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                shoes
              </button>
              <button 
                onClick={() => {
                  setSearchQuery('sneakers men');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                sneakers men
              </button>
              <button 
                onClick={() => {
                  setSearchQuery('running shoes');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                running shoes
              </button>
              <button 
                onClick={() => {
                  setSearchQuery('jordan');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                jordan
              </button>
              <button 
                onClick={() => {
                  setSearchQuery('air jordan 1');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                air jordan 1
              </button>
              <button 
                onClick={() => {
                  setSearchQuery('air force 1');
                  handleSearch({ key: 'Enter' });
                  setMobileSearchOpen(false);
                }}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
              >
                air force 1
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Sidebar */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
            onClick={() => {
              setMobileMenuOpen(false);
              setMobileSubmenu(null);
            }}
          ></div>
          
          {/* Sidebar */}
          <div className="fixed top-0 right-0 h-full w-full max-w-sm bg-white z-50 shadow-2xl overflow-hidden lg:hidden transform transition-transform duration-300">
            {/* Main Menu */}
            <div className={`h-full transition-transform duration-300 ${mobileSubmenu ? '-translate-x-full' : 'translate-x-0'}`}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-black flex items-center justify-center">
                    <span className="text-white font-bold text-lg">H</span>
                  </div>
                  <span className="font-bold text-lg">House of PM</span>
                </div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setMobileSubmenu(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-full transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              {user && (
                <div className="px-4 py-3 bg-indigo-50 border-b">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{user.full_name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{user.full_name}</p>
                      <p className="text-xs text-gray-600">{user.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Navigation */}
              <nav className="overflow-y-auto" style={{height: user ? 'calc(100% - 145px)' : 'calc(100% - 73px)'}}>
                <ul className="py-2">
                  <li className="border-b">
                    <button 
                      onClick={() => setMobileSubmenu('featured')}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">New & Featured</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                  <li className="border-b">
                    <button 
                      onClick={() => setMobileSubmenu('men')}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">Men</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                  <li className="border-b">
                    <button 
                      onClick={() => setMobileSubmenu('women')}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">Women</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                  <li className="border-b">
                    <button 
                      onClick={() => setMobileSubmenu('kids')}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">Kids</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                  <li className="border-b">
                    <button 
                      onClick={() => setMobileSubmenu('sale')}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-red-600">Sale</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                  
                  {/* Quick Links */}
                  <li className="mt-4 px-4 py-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quick Links</p>
                  </li>
                  {user ? (
                    <>
                      <li>
                        <Link 
                          to="/orders" 
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileSubmenu(null);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <span>My Orders</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/cart" 
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileSubmenu(null);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span>Shopping Cart</span>
                            {cartItemCount > 0 && (
                              <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5 font-semibold">
                                {cartItemCount}
                              </span>
                            )}
                          </div>
                        </Link>
                      </li>
                      {user?.role === 'admin' && (
                        <li>
                          <Link 
                            to="/admin" 
                            className="block px-4 py-3 text-sm text-red-600 font-semibold hover:bg-red-50 transition-colors"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSubmenu(null);
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>Admin Panel</span>
                            </div>
                          </Link>
                        </li>
                      )}
                      <li className="border-t mt-2">
                        <button 
                          onClick={() => {
                            logout();
                            setMobileMenuOpen(false);
                            setMobileSubmenu(null);
                          }} 
                          className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>Logout</span>
                          </div>
                        </button>
                      </li>
                    </>
                  ) : (
                    <>
                      <li>
                        <Link 
                          to="/login" 
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileSubmenu(null);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                            </svg>
                            <span>Log in</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/register" 
                          className="block px-4 py-3 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileSubmenu(null);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            <span>Sign up</span>
                          </div>
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </nav>
            </div>

            {/* Submenu Panel */}
            <div className={`absolute top-0 left-0 w-full h-full bg-white transition-transform duration-300 ${mobileSubmenu ? 'translate-x-0' : 'translate-x-full'}`}>
              {/* Back Button */}
              <div className="flex items-center p-4 border-b bg-gray-50">
                <button
                  onClick={() => setMobileSubmenu(null)}
                  className="p-2 hover:bg-gray-200 rounded-full transition-all mr-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-lg font-bold">
                  {mobileSubmenu === 'featured' && 'New & Featured'}
                  {mobileSubmenu === 'men' && 'Men'}
                  {mobileSubmenu === 'women' && 'Women'}
                  {mobileSubmenu === 'kids' && 'Kids'}
                  {mobileSubmenu === 'sale' && 'Sale'}
                </h2>
              </div>

              {/* Submenu Content */}
              <div className="overflow-y-auto bg-gray-50" style={{height: 'calc(100% - 73px)'}}>
                {mobileSubmenu === 'featured' && (
                  <div className="p-4 space-y-6">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <h3 className="font-bold text-sm mb-3 text-gray-900">Featured</h3>
                      <ul className="space-y-3 text-sm">
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>New Arrivals</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Bestsellers</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Trending</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <h3 className="font-bold text-sm mb-3 text-gray-900">Shop By Sport</h3>
                      <ul className="space-y-3 text-sm">
                        <li><Link to="/products?category=Sports" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Running</Link></li>
                        <li><Link to="/products?category=Sports" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Basketball</Link></li>
                        <li><Link to="/products?category=Sports" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Football</Link></li>
                      </ul>
                    </div>
                  </div>
                )}

                {mobileSubmenu === 'men' && (
                  <div className="p-4 space-y-4">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/topwear" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Topwear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/topwear/tshirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>T-Shirts</Link></li>
                        <li><Link to="/mens/topwear/casual-shirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Casual Shirts</Link></li>
                        <li><Link to="/mens/topwear/formal-shirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Formal Shirts</Link></li>
                        <li><Link to="/mens/topwear/sweatshirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sweatshirts</Link></li>
                        <li><Link to="/mens/topwear/sweaters" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sweaters</Link></li>
                        <li><Link to="/mens/topwear/jackets" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jackets</Link></li>
                        <li><Link to="/mens/topwear/blazers-coats" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Blazers & Coats</Link></li>
                        <li><Link to="/mens/topwear/suits" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Suits</Link></li>
                        <l><Link to="/mens/topwear/rain-jackets" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Rain Jackets</Link></l>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/bottomwear" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Bottomwear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/bottomwear/jeans" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jeans</Link></li>
                        <li><Link to="/mens/bottomwear/casual-trousers" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Casual Trousers</Link></li>
                        <li><Link to="/mens/bottomwear/formal-trousers" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Formal Trousers</Link></li>
                        <li><Link to="/mens/bottomwear/shorts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shorts</Link></li>
                        <li><Link to="/mens/bottomwear/track-pants-joggers" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Track Pants & Joggers</Link></li>
                      </ul>
                      <Link to="/topwear" className="font-bold text-sm mb-3 mt-4 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Plus Size</Link>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/indian-wear" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Indian & Festive Wear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/indian-wear/kurtas-kurta-sets" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Kurtas & Kurta Sets</Link></li>
                        <li><Link to="/mens/indian-wear/sherwanis" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sherwanis</Link></li>
                        <li><Link to="/mens/indian-wear/nehru-jackets" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Nehru Jackets</Link></li>
                        <li><Link to="/mens/indian-wear/dhotis" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dhotis</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/sports-wear" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sports & Active Wear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/sports-active-wear/active-tshirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Active T-Shirts</Link></li>
                        <li><Link to="/mens/sports-active-wear/track-pants-shorts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Track Pants & Shorts</Link></li>
                        <li><Link to="/mens/sports-active-wear/tracksuits" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tracksuits</Link></li>
                        <li><Link to="/mens/sports-active-wear/jackets-sweatshirts" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jackets & Sweatshirts</Link></li>
                        <li><Link to="/mens/sports-active-wear/swimwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Swimwear</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/innerwear-sleepwear" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Innerwear & Sleepwear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/innerwear-sleepwear/briefs-trunks" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Briefs & Trunks</Link></li>
                        <li><Link to="/mens/innerwear-sleepwear/boxers" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Boxers</Link></li>
                        <li><Link to="/mens/innerwear-sleepwear/vests" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Vests</Link></li>
                        <li><Link to="/mens/innerwear-sleepwear/sleepwear-loungewear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sleepwear & Loungewear</Link></li>
                        <li><Link to="/mens/innerwear-sleepwear/thermals" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Thermals</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/fashion-accessories" className="font-bold text-sm mb-3 text-red-500 hover:text-red-700 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Fashion Accessories</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/mens/fashion-accessories/ties-cufflinks-pocket-squares" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Ties, Cufflinks & Pocket Squares</Link></li>
                        <li><Link to="/mens/fashion-accessories/caps-hats" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Caps & Hats</Link></li>           
                        <li><Link to="/mens/fashion-accessories/mufflers-scarves-gloves" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Mufflers, Scarves & Gloves</Link></li>
                        <li><Link to="/mens/fashion-accessories/mufflers-scarves-gloves" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Mufflers, Scarves & Gloves</Link></li>
                      </ul>
                    </div>
                  </div>
                )}

                {mobileSubmenu === 'women' && (
                  <div className="p-4 space-y-4">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/indian-fusion-wear" className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Indian & Fusion Wear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Kurtas & Suits</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Kurtis, Tunics & Tops</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sarees</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Ethnic Wear</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Leggings, Salwars & Churidars</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Skirts & Palazzos</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dress Materials</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Lehenga Cholis</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dupattas & Shawls</Link></li>
                        <li><Link to="/indian-fusion-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jackets</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/western-wear" className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Western Wear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dresses</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tops</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tshirts</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jeans</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Trousers & Capris</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shorts & Skirts</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Co-ords</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Playsuits</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jumpsuits</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shrugs</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sweaters & Sweatshirts</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jackets & Coats</Link></li>
                        <li><Link to="/western-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Blazers & Waistcoats</Link></li>
                      </ul>
                      <h3 className="font-bold text-sm mb-3 mt-4 text-pink-600">Plus Size</h3>
                      <h3 className="font-bold text-sm mb-3 mt-2 text-pink-600">Maternity</h3>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/sports-active-wear" className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sports & Active Wear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/sports-active-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sports T-Shirts & Tops</Link></li>
                        <li><Link to="/sports-active-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Track Pants & Shorts</Link></li>
                        <li><Link to="/sports-active-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tracksuits</Link></li>
                        <li><Link to="/sports-active-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sports Jackets</Link></li>
                        <li><Link to="/sports-active-wear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Swimwear</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/lingerie-sleepwear" className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Lingerie & Sleepwear</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Bra</Link></li>
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Briefs</Link></li>
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shapewear</Link></li>
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Sleepwear & Loungewear</Link></li>
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Swimwear</Link></li>
                        <li><Link to="/lingerie-sleepwear" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Camisoles & Thermals</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/women-fashion-accessories" className="font-bold text-sm mb-3 text-pink-600 hover:text-pink-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Fashion Accessories</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/women-fashion-accessories" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Scarves & Stoles</Link></li>
                        <li><Link to="/women-fashion-accessories" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Caps & Hats</Link></li>
                        <li><Link to="/women-fashion-accessories" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Gloves</Link></li>
                      </ul>
                    </div>
                  </div>
                )}

                {mobileSubmenu === 'kids' && (
                  <div className="p-4 space-y-4">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/boys-clothing" className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Boys Clothing</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>T-Shirts</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shirts</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shorts</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jeans</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Trousers</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Clothing Sets</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Ethnic Wear</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Track Pants & Pyjamas</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jacket, Sweater & Sweatshirts</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Party Wear</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Innerwear & Thermals</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Nightwear & Loungewear</Link></li>
                        <li><Link to="/boys-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Value Packs</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/girls-clothing" className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Girls Clothing</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dresses</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tops</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tshirts</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Clothing Sets</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Lehenga choli</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Kurta Sets</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Party wear</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dungarees & Jumpsuits</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Skirts & shorts</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tights & Leggings</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jeans, Trousers & Capris</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Jacket, Sweater & Sweatshirts</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Innerwear & Thermals</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Nightwear & Loungewear</Link></li>
                        <li><Link to="/girls-clothing" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Value Packs</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <Link to="/infants" className="font-bold text-sm mb-3 text-orange-600 hover:text-orange-800 block" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Infants</Link>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Bodysuits</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Rompers & Sleepsuits</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Clothing Sets</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Tshirts & Tops</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Dresses</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Bottom wear</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Winter Wear</Link></li>
                        <li><Link to="/infants" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Innerwear & Sleepwear</Link></li>
                      </ul>
                      <h3 className="font-bold text-sm mb-3 mt-4 text-orange-600">Brands</h3>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>H&M</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Max Kids</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Pantaloons</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>United Colors Of Benetton Kids</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>YK</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>U.S. Polo Assn. Kids</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Mothercare</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>HRX</Link></li>
                      </ul>
                    </div>
                  </div>
                )}

                {mobileSubmenu === 'sale' && (
                  <div className="p-4 space-y-4">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <h3 className="font-bold text-sm mb-3 text-red-600">Shop By Discount</h3>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Up to 40% Off</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>40% - 60% Off</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>60% and More</Link></li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <h3 className="font-bold text-sm mb-3 text-red-600">Shop By Product</h3>
                      <ul className="space-y-2 text-sm">
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Shoes</Link></li>
                        <li><Link to="/products" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Clothing</Link></li>
                        <li><Link to="/products?category=Accessories" className="text-gray-700 hover:text-black block py-1" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>Accessories</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Promo Bar */}
      <div className="bg-red-600 text-white text-center py-2">
        <p className="text-sm font-semibold uppercase tracking-wide">
          BUY 2 GET 70% OFF!
        </p>
      </div>
    </>
  );
};

export default Navbar;
